import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/shared/lib/utils";

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return (
    <DialogPrimitive.Root
      data-slot="dialog"
      {...props}
    />
  );
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return (
    <DialogPrimitive.Trigger
      data-slot="dialog-trigger"
      {...props}
    />
  );
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return (
    <DialogPrimitive.Portal
      data-slot="dialog-portal"
      {...props}
    />
  );
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return (
    <DialogPrimitive.Close
      data-slot="dialog-close"
      {...props}
    />
  );
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[100] bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className, children, showClose = true, hasTitle = false, allowCKEditorFocus = false, ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showClose?: boolean;
  hasTitle?: boolean;
  allowCKEditorFocus?: boolean;
}) {
  // Function to check if the element is part of CKEditor
  const isCKEditorElement = (element: Element | null): boolean => {
    if (!element) return false;

    return !!(
      element.closest?.('.ck-balloon-panel') ||
      element.closest?.('.ck-toolbar') ||
      element.closest?.('.ck-button') ||
      element.closest?.('.ck-dropdown') ||
      element.closest?.('.ck-tooltip') ||
      element.closest?.('.ck-color-picker') ||
      element.closest?.('.ck-link-form') ||
      element.closest?.('.ck-link-actions') ||
      element.closest?.('.ck-input') ||
      element.classList?.contains('ck-balloon-panel') ||
      element.classList?.contains('ck-toolbar') ||
      element.classList?.contains('ck-button') ||
      element.classList?.contains('ck-dropdown') ||
      element.classList?.contains('ck-link-form') ||
      element.classList?.contains('ck-link-actions') ||
      element.classList?.contains('ck-input')
    );
  };

  // Custom handlers for focus and pointer events
  // These allow CKEditor elements to receive focus/pointer events when allowCKEditorFocus is enabled
  const handleFocusOutside = (event: CustomEvent) => {
    if (allowCKEditorFocus && isCKEditorElement(event.target as Element)) {
      // Don't prevent - let CKEditor handle focus naturally
    }
  };

  const handlePointerDownOutside = (event: CustomEvent) => {
    if (allowCKEditorFocus && isCKEditorElement(event.target as Element)) {
      // Don't prevent - let CKEditor elements receive normal pointer events
    }
  };

  // Effect to manage tabindex for CKEditor compatibility
  React.useEffect(() => {
    if (!allowCKEditorFocus) return;

    const dialogContent = document.querySelector('[data-slot="dialog-content"]') as HTMLElement;
    if (!dialogContent) return;

    // Store original tabindex
    const originalTabIndex = dialogContent.getAttribute('tabindex');

    // Remove or modify tabindex that might interfere with CKEditor
    if (dialogContent.getAttribute('tabindex') === '-1') {
      dialogContent.removeAttribute('tabindex');
    }

    return () => {
      // Restore original tabindex on cleanup
      if (originalTabIndex !== null) {
        dialogContent.setAttribute('tabindex', originalTabIndex);
      }
    };
  }, [allowCKEditorFocus]);

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-[100] grid w-full max-w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          className,
        )}
        onFocusOutside={handleFocusOutside}
        onPointerDownOutside={handlePointerDownOutside}
        {...props}
      >
        {children}
        {showClose && (
          <DialogPrimitive.Close className={cn(
            "cursor-pointer ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-2 right-2 rounded-md opacity-70 transition-all hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-6 p-2",
            hasTitle
              ? "border border-gray-200 hover:border-gray-300"
              : "bg-gray-50 hover:bg-gray-100"
          )}>
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
